import { useLinkTo } from '@react-navigation/native'
import { createNativeStackNavigator } from '@react-navigation/native-stack'
import { getToken } from 'firebase/messaging'
import { fetchAndActivate, getValue } from 'firebase/remote-config'
import React, { useEffect, useMemo } from 'react'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useLocalStorage } from 'hooks/useLocalStorage'

import { remoteConfig, webMessaging } from 'utils/firebase/initialize.web'

import { useAuth } from 'contexts/AuthContext'

import {
  RootStackParamList,
  computeCurrentRoutes,
  updatePushNotificationTokensForUser,
} from './utils'

const RootStackNavigator = createNativeStackNavigator<RootStackParamList>()

export const RootStack = () => {
  const { meta, data: currentUser } = useCurrentUser()

  const [currentFCMToken, setCurrentFCMToken] = useLocalStorage('fcmToken', null)

  /**
   * If the user is logged in and has completed onboarding, then we request push notification permission.
   */
  useEffect(() => {
    if (meta?.onboardingComplete && currentFCMToken === null) {
      if (remoteConfig && webMessaging) {
        fetchAndActivate(remoteConfig).then(async () => {
          // VAPID Keys are Voluntary Application Server Identification keys.
          // We generate one for each environment in the Firebase console for FCM
          // (Firebase Cloud Message) and store it in Remote Config for easy access
          // and rotation if necessary.
          const vapidKey = getValue(remoteConfig, 'vapidkey')
          getToken(webMessaging, { vapidKey: vapidKey.asString() })
            .then(async (currentToken) => {
              setCurrentFCMToken(currentToken)

              if (currentToken && currentUser) {
                await updatePushNotificationTokensForUser(currentUser, currentToken)
              } else {
                // Permission request UI will show here.
                console.log('No registration token available. Request permission to generate one.')
              }
            })
            .catch((err) => {
              console.log('An error occurred while retrieving token or setting FCM token. ', err)
            })
        })
      }
    }
  }, [meta, currentUser, currentFCMToken, setCurrentFCMToken])

  const linkTo = useLinkTo()
  const { initialRoute, setInitialRoute } = useAuth()

  const currentRoutes = useMemo(() => computeCurrentRoutes(RootStackNavigator, meta), [meta])

  useEffect(() => {
    if (initialRoute && initialRoute !== '' && meta.loggedIn && meta.onboardingComplete) {
      const redirectRoute = initialRoute
      setInitialRoute('')
      try {
        setTimeout(() => linkTo(redirectRoute)) // HACK: Makes navigation wait a split second for routes to load in.
      } catch (error) {
        console.error(error)
        setInitialRoute('')
      }
    }
  }, [initialRoute, linkTo, meta.loggedIn, meta.onboardingComplete, setInitialRoute])

  return <RootStackNavigator.Navigator>{currentRoutes}</RootStackNavigator.Navigator>
}
